import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  MenuItem,
  CircularProgress,
  Stack,
  Chip,
  Snackbar,
  Alert,
  Collapse,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@mui/material'
import { payoutService, Payout } from '../services/payoutService'
import { useNavigate } from 'react-router-dom'
import { Refresh as RefreshIcon, Search as SearchIcon, Add as AddIcon, Upload as UploadIcon, Download as DownloadIcon, CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon, HighlightOff as HighlightOffIcon } from '@mui/icons-material'

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PENDING', label: 'Pending' },
]

const Payouts: React.FC = () => {
  const [data, setData] = useState<Payout[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'success' | 'error' | 'warning' | 'info' })
  const [errorDetails, setErrorDetails] = useState<{ title?: string; details?: string[] } | null>(null)



  // Create Payout form state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<any>({
    riderPublicID: '',
    clientName: '',
    beneficiaryName: '',
    beneficiaryAccountNo: '',
    ifsc: '',
    transactionType: '',
    debitAccountNo: '',
    transactionDate: '', // use input type datetime-local
    amount: '',
    remarks: '',
    utrNumber: '',
    status: '',
    periodStartDate: '',
    periodEndDate: '',
  })
  const [formErrors, setFormErrors] = useState<any>({})
  const [generateOpen, setGenerateOpen] = useState(false)
  const [misFile, setMisFile] = useState<File | null>(null)
  const [bankFile, setBankFile] = useState<File | null>(null)
  const [generateLoading, setGenerateLoading] = useState(false)
  const [generateErrors, setGenerateErrors] = useState<{ misFile?: string; bankFile?: string }>({})
  const [exportLoading, setExportLoading] = useState(false)
  const [dragOverMis, setDragOverMis] = useState(false)
  const [dragOverBank, setDragOverBank] = useState(false)

  const handleDragOver = (e: React.DragEvent, setDragOver: (val: boolean) => void) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (setDragOver: (val: boolean) => void) => {
    setDragOver(false)
  }

  const handleDropMis = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverMis(false)
    const file = e.dataTransfer.files?.[0]
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setMisFile(file)
      setGenerateErrors((prev) => ({ ...prev, misFile: undefined }))
    } else {
      setGenerateErrors((prev) => ({ ...prev, misFile: 'Please select a valid Excel file (.xlsx or .xls)' }))
    }
  }

  const handleDropBank = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverBank(false)
    const file = e.dataTransfer.files?.[0]
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setBankFile(file)
      setGenerateErrors((prev) => ({ ...prev, bankFile: undefined }))
    } else {
      setGenerateErrors((prev) => ({ ...prev, bankFile: 'Please select a valid Excel file (.xlsx or .xls)' }))
    }
  }

  const FileUploadBox = ({ 
    file, 
    setFile, 
    label, 
    error, 
    dragOver, 
    setDragOverFn, 
    onDrop 
  }: { 
    file: File | null; 
    setFile: (f: File) => void; 
    label: string; 
    error?: string; 
    dragOver: boolean;
    setDragOverFn: (val: boolean) => void;
    onDrop: (e: React.DragEvent) => void;
  }) => (
    <Box
      onDragOver={(e) => handleDragOver(e, setDragOverFn)}
      onDragLeave={() => handleDragLeave(setDragOverFn)}
      onDrop={onDrop}
      sx={{
        border: '2px solid',
        borderColor: error ? '#ff6b6b' : dragOver ? '#1976d2' : '#bdbdbd',
        borderRadius: 1,
        p: 3,
        textAlign: 'center',
        backgroundColor: file ? '#e8f5e9' : dragOver ? '#e3f2fd' : '#f5f5f5',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          borderColor: '#1976d2',
          backgroundColor: '#e3f2fd',
          boxShadow: '0 4px 8px rgba(25, 118, 210, 0.12)',
        },
      }}
      component="label"
    >
      <input
        type="file"
        hidden
        accept=".xlsx,.xls"
        onChange={(event) => {
          if (event.target.files?.[0]) {
            setFile(event.target.files[0])
            setGenerateErrors((prev) => ({ ...prev, [label]: undefined }))
          }
        }}
      />
      
      <Stack spacing={2} alignItems="center">
        {file ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 52, color: '#4caf50' }} />
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#1b5e20', wordBreak: 'break-word' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 52, color: '#1976d2' }} />
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#0d47a1' }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Drag & drop or click to select
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  )

  const setField = (key: string, value: any) => setForm((s: any) => ({ ...s, [key]: value }))

  const validateForm = () => {
    const errors: any = {}
    if (!form.riderPublicID) errors.riderPublicID = 'Required'
    if (!form.clientName) errors.clientName = 'Required'
    if (!form.beneficiaryName) errors.beneficiaryName = 'Required'
    // if (!form.beneficiaryAccountNo) errors.beneficiaryAccountNo = 'Required'
    // if (!form.ifsc) errors.ifsc = 'Required'
    // if (!form.transactionType) errors.transactionType = 'Required'
    // if (!form.debitAccountNo) errors.debitAccountNo = 'Required'
    if (!form.transactionDate) errors.transactionDate = 'Required'
    if (!form.amount || Number(form.amount) <= 0) errors.amount = 'Invalid amount'
    if (!form.status) errors.status = 'Required'
    if (String(form.status).toUpperCase() === 'SUCCESS') {
      if (!form.periodStartDate) errors.periodStartDate = 'Required when status is SUCCESS'
      if (!form.periodEndDate) errors.periodEndDate = 'Required when status is SUCCESS'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const clearGenerateForm = () => {
    setMisFile(null)
    setBankFile(null)
    setGenerateErrors({})
  }

  const handleGenerateFile = async () => {
    const errors: { misFile?: string; bankFile?: string } = {}
    if (!misFile) errors.misFile = 'MIS file is required'
    if (!bankFile) errors.bankFile = 'Bank file is required'
    setGenerateErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    setGenerateLoading(true)
    try {
      const { blob, filename } = await payoutService.generatePayoutFile(misFile!, bankFile!)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      setSnackbar({ open: true, message: 'Payout file downloaded successfully', severity: 'success' })
      setGenerateOpen(false)
      clearGenerateForm()
    } catch (err: any) {
      console.error('Generate payout file failed', err)
      
      // Extract error message
      let errorMessage = 'Failed to generate payout file'
      let errorTitle = 'Generation Error'
      let errorDetails: string[] = []
      const statusCode = err?.status
      
      if (err?.message) {
        errorMessage = err.message
        
        // Parse column mismatch errors
        if (errorMessage.includes('Missing required columns')) {
          errorTitle = 'Excel File Column Mismatch'
          errorDetails = [
            errorMessage,
            '',
            'Common issues:',
            '• Check for spelling mistakes in column headers',
            '• Ensure headers are in the first row',
            '• Common typos: "periodstartddate" should be "periodstartdate"',
            '• All column names are case-insensitive but spelling must match exactly',
          ]
        } else if (errorMessage.includes('must contain at least')) {
          errorTitle = 'Invalid File Format'
          errorDetails = [
            errorMessage,
            '',
            'Make sure your Excel file:',
            '• Has column headers in the first row',
            '• Contains at least one data row (starting from row 2)',
            '• Is in valid .xlsx format',
          ]
        }
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      }
      
      // Append status code if available and not already in message
      if (statusCode && !errorMessage.includes(statusCode)) {
        if (statusCode === 403) {
          errorMessage = `Access Denied (403): ${errorMessage}`
          errorTitle = 'Permission Error'
        } else if (statusCode === 400) {
          errorTitle = 'Validation Error'
          errorMessage = `Invalid Request (400): ${errorMessage}`
        } else if (statusCode === 500) {
          errorMessage = `Server Error (500): ${errorMessage}`
          errorTitle = 'Server Error'
        } else {
          errorMessage = `Error ${statusCode}: ${errorMessage}`
        }
      }
      
      setErrorDetails({ title: errorTitle, details: errorDetails.length > 0 ? errorDetails : [errorMessage] })
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setGenerateLoading(false)
    }
  }

  const handleExportBankDetails = async () => {
    setExportLoading(true)
    try {
      const { blob, filename } = await payoutService.exportRiderBankDetails()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      setSnackbar({ open: true, message: 'Rider bank details downloaded successfully', severity: 'success' })
    } catch (err: any) {
      console.error('Export rider bank details failed', err)
      let errorMessage = 'Failed to download rider bank details'
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.message) {
        errorMessage = err.message
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setExportLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!validateForm()) return
    setCreating(true)
    try {
      // Normalize values
      const payload: any = {
        riderPublicID: form.riderPublicID,
        clientName: form.clientName,
        beneficiaryName: form.beneficiaryName,
        beneficiaryAccountNo: form.beneficiaryAccountNo,
        ifsc: form.ifsc,
        transactionType: form.transactionType,
        debitAccountNo: form.debitAccountNo,
        transactionDate: form.transactionDate ? new Date(form.transactionDate).toISOString() : undefined,
        amount: Number(form.amount),
        remarks: form.remarks || undefined,
        utrNumber: form.utrNumber || undefined,
        status: form.status,
      }

      if (String(form.status).toUpperCase() === 'SUCCESS') {
        payload.periodStartDate = form.periodStartDate
        payload.periodEndDate = form.periodEndDate
      }

      await payoutService.createPayout(payload)
      setSnackbar({ open: true, message: 'Payout created successfully', severity: 'success' })
      setShowCreateForm(false)
      setForm({
        riderPublicID: '',
        clientName: '',
        beneficiaryName: '',
        beneficiaryAccountNo: '',
        ifsc: '',
        transactionType: '',
        debitAccountNo: '',
        transactionDate: '',
        amount: '',
        remarks: '',
        utrNumber: '',
        status: '',
        periodStartDate: '',
        periodEndDate: '',
      })
      // Refresh list
      setPage(0)
      await load()
    } catch (err: any) {
      console.error('Create payout failed', err)
      
      // Extract error message
      let errorMessage = 'Failed to create payout'
      const statusCode = err?.response?.status
      
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.message) {
        errorMessage = err.message
      }
      
      // Append status code if available and not already in message
      if (statusCode && !errorMessage.includes(statusCode)) {
        if (statusCode === 403) {
          errorMessage = `Access Denied (403): ${errorMessage}`
        } else if (statusCode === 400) {
          errorMessage = `Invalid Request (400): ${errorMessage}`
        } else if (statusCode === 409) {
          errorMessage = `Conflict (409): ${errorMessage}`
        } else if (statusCode === 500) {
          errorMessage = `Server Error (500): ${errorMessage}`
        } else {
          errorMessage = `Error ${statusCode}: ${errorMessage}`
        }
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch ((status || '').toUpperCase()) {
      case 'SUCCESS':
        return 'success'
      case 'FAILED':
        return 'error'
      case 'PENDING':
        return 'warning'
      default:
        return 'default'
    }
  }

  const navigate = useNavigate()

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await payoutService.getAllPayouts({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        clientName: clientFilter || undefined,
        sortBy: 'transactionDate',
        sortOrder: 'desc',
      })

      // Backend returns: { message, total, page, limit, data }
      setData(res.data || [])
      setTotal(res.total || 0)
    } catch (err: any) {
      console.error('Failed to load payouts', err)
      
      // Extract error message
      let errorMessage = 'Failed to load payouts'
      const statusCode = err?.response?.status
      
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }
      
      // Append status code if available
      if (statusCode && !errorMessage.includes(statusCode)) {
        if (statusCode === 403) {
          errorMessage = `Access Denied (403): You don't have permission to view payouts`
        } else if (statusCode === 500) {
          errorMessage = `Server Error (500): ${errorMessage}`
        }
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, statusFilter, clientFilter])

  useEffect(() => {
    load()
  }, [load])



  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>Payouts History</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateForm((s) => !s)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Create
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportBankDetails} disabled={exportLoading} sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {exportLoading ? 'Exporting...' : 'Export Details'}
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setGenerateOpen(true)} sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Generate File
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => navigate('/payouts/bulk-upload')} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Upload
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => load()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Refresh
          </Button>
        </Stack>
      </Box>

          <Collapse in={showCreateForm}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Rider Public ID"
                      value={form.riderPublicID}
                      onChange={(e) => setField('riderPublicID', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.riderPublicID}
                      helperText={formErrors.riderPublicID}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Client Name"
                      value={form.clientName}
                      onChange={(e) => setField('clientName', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.clientName}
                      helperText={formErrors.clientName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Beneficiary Name"
                      value={form.beneficiaryName}
                      onChange={(e) => setField('beneficiaryName', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.beneficiaryName}
                      helperText={formErrors.beneficiaryName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Beneficiary Account"
                      value={form.beneficiaryAccountNo}
                      onChange={(e) => setField('beneficiaryAccountNo', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.beneficiaryAccountNo}
                      helperText={formErrors.beneficiaryAccountNo}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="IFSC"
                      value={form.ifsc}
                      onChange={(e) => setField('ifsc', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.ifsc}
                      helperText={formErrors.ifsc}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Transaction Type"
                      select
                      value={form.transactionType}
                      onChange={(e) => setField('transactionType', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.transactionType}
                      helperText={formErrors.transactionType}
                    >
                      {['NEFT', 'RTGS', 'IMPS', 'OTHER'].map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Debit Account"
                      value={form.debitAccountNo}
                      onChange={(e) => setField('debitAccountNo', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.debitAccountNo}
                      helperText={formErrors.debitAccountNo}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Transaction Date"
                      type="datetime-local"
                      value={form.transactionDate}
                      onChange={(e) => setField('transactionDate', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.transactionDate}
                      helperText={formErrors.transactionDate}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Amount"
                      type="number"
                      value={form.amount}
                      onChange={(e) => setField('amount', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Status"
                      select
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                      size="small"
                      fullWidth
                      error={!!formErrors.status}
                      helperText={formErrors.status}
                    >
                      {STATUSES.filter((s) => s.value).map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="UTR"
                      value={form.utrNumber}
                      onChange={(e) => setField('utrNumber', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Period Start"
                      type="date"
                      value={form.periodStartDate}
                      onChange={(e) => setField('periodStartDate', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.periodStartDate}
                      helperText={formErrors.periodStartDate}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Period End"
                      type="date"
                      value={form.periodEndDate}
                      onChange={(e) => setField('periodEndDate', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.periodEndDate}
                      helperText={formErrors.periodEndDate}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Remarks"
                      value={form.remarks}
                      onChange={(e) => setField('remarks', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                </Grid>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button onClick={() => setShowCreateForm(false)} disabled={creating} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                  <Button variant="contained" onClick={handleCreate} disabled={creating} startIcon={creating ? <CircularProgress size={16} /> : null} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    Create
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Collapse>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  label="Status"
                  select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 160 } }}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Client Name"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                />

                <Button variant="contained" onClick={() => { setPage(0); load() }} startIcon={<SearchIcon />} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  Search
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Paper>
            <TableContainer sx={{ overflowX: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow sx={{ }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Rider ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Beneficiary</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' }, textAlign: 'right' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>Txn Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>UTR</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row) => (
                      <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: { xs: '#fafafa', sm: '#f5f5f5' } } }}>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>{row.riderPublicID}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{row.clientName}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{row.beneficiaryName}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, textAlign: 'right', fontWeight: 500 }}>₹{row.amount}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>{new Date(row.transactionDate).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                          <Chip
                            label={row.status}
                            color={getStatusColor(row.status) as any}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>{row.utrNumber || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {data.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No payouts found.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: 1,
                },
                '& .MuiTablePagination-select': {
                  width: { xs: '100%', sm: 'auto' },
                },
              }}
            />
          </Paper>




      <Dialog 
        open={generateOpen} 
        onClose={() => {
          setGenerateOpen(false)
          clearGenerateForm()
        }} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            mx: { xs: 2, sm: 0 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              <DownloadIcon sx={{ color: 'primary.main' }} />
              Generate Payout File
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload MIS and Bank Excel files to generate merged payout data
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    MIS File <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <FileUploadBox
                    file={misFile}
                    setFile={setMisFile}
                    label="Select MIS Excel file"
                    error={generateErrors.misFile}
                    dragOver={dragOverMis}
                    setDragOverFn={setDragOverMis}
                    onDrop={handleDropMis}
                  />
                  {generateErrors.misFile && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, alignItems: 'center' }}>
                      <HighlightOffIcon sx={{ fontSize: 18, color: 'error.main' }} />
                      <FormHelperText error>{generateErrors.misFile}</FormHelperText>
                    </Stack>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Bank File <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <FileUploadBox
                    file={bankFile}
                    setFile={setBankFile}
                    label="Select Bank Excel file"
                    error={generateErrors.bankFile}
                    dragOver={dragOverBank}
                    setDragOverFn={setDragOverBank}
                    onDrop={handleDropBank}
                  />
                  {generateErrors.bankFile && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, alignItems: 'center' }}>
                      <HighlightOffIcon sx={{ fontSize: 18, color: 'error.main' }} />
                      <FormHelperText error>{generateErrors.bankFile}</FormHelperText>
                    </Stack>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button variant="outlined" onClick={clearGenerateForm} disabled={generateLoading}>
                    Reset
                  </Button>
                  <Button variant="contained" onClick={handleGenerateFile} disabled={generateLoading || !misFile || !bankFile} startIcon={generateLoading ? <CircularProgress size={18} /> : <CloudUploadIcon />}>
                    {generateLoading ? 'Generating...' : 'Generate & Download'}
                  </Button>
                </Stack>
              </Grid>
              </Grid>
                <Card sx={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  📋 How it works:
                </Typography>
                <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2, mb: 1.5 }}>
                  <Typography component="li" variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Uploads both Excel files for processing
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Matches records by composite ID (beneficiary + account + IFSC + amount)
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Generates a merged payout file with matched records
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Marks duplicates with "DUPLICATE" status
                  </Typography>
                </Stack>

                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, mt: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  ✅ Required Column Names:
                </Typography>
                <Box sx={{ backgroundColor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #ddd' }}>
                  <Typography variant="caption" fontWeight={600} color="#1565c0" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    MIS File must have:
                  </Typography>
                  <Typography variant="caption" sx={{ pl: 1, display: 'block', lineHeight: 1.4, color: '#666', fontSize: { xs: '0.7rem', sm: '0.875rem' }, overflowWrap: 'break-word' }}>
                    RiderName, riderPublicRiderId, clientName, clientRiderId, PeriodStartDate, PeriodEndDate, month, year, Hub, City, accountHolderName, accountNumber, ifscCode, finalPayout
                  </Typography>
                </Box>

                <Box sx={{ backgroundColor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #ddd', mt: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="#1565c0" sx={{ display: 'block', mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Bank File must have:
                  </Typography>
                  <Typography variant="caption" sx={{ pl: 1, display: 'block', lineHeight: 1.4, color: '#666', fontSize: { xs: '0.7rem', sm: '0.875rem' }, overflowWrap: 'break-word' }}>
                    Beneficiary Name, Beneficiary Account Number, IFSC, Debit Account No, Transaction Date, Amount, Remarks, UTR Number
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 1.5, py: 1, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                  <Typography variant="caption">
                    💡 Column names are case-insensitive but spelling must match exactly. Check for typos like "periodstartddate" vs "periodstartdate"
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button 
            onClick={() => {
              setGenerateOpen(false)
              clearGenerateForm()
            }} 
            disabled={generateLoading}
            variant="outlined"
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleGenerateFile} 
            disabled={generateLoading || !misFile || !bankFile}
            startIcon={generateLoading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            {generateLoading ? 'Generating...' : 'Generate & Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={!!errorDetails} 
        onClose={() => setErrorDetails(null)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            mx: { xs: 2, sm: 0 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, backgroundColor: '#fff3e0', borderBottom: '2px solid #ff9800' }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#e65100', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              ⚠️ {errorDetails?.title || 'Error Details'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={2}>
            {errorDetails?.details?.map((detail, idx) => (
              <Box key={idx}>
                {detail === '' ? (
                  <Box sx={{ height: 8 }} />
                ) : detail.startsWith('•') ? (
                  <Typography variant="body2" sx={{ pl: 2, color: '#555', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {detail}
                  </Typography>
                ) : detail.includes(':') || idx === 0 ? (
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#d32f2f', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {detail}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {detail}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button 
            onClick={() => setErrorDetails(null)}
            variant="contained"
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => {
          setSnackbar({ ...snackbar, open: false })
          if (errorDetails) {
            // Show error details dialog when snackbar closes if there are details
            setTimeout(() => {}, 100)
          }
        }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ cursor: errorDetails ? 'pointer' : 'default' }}
          onClick={() => errorDetails && setErrorDetails(errorDetails)}
        >
          {snackbar.message}
          {errorDetails && snackbar.severity === 'error' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Click to see detailed error information
            </Typography>
          )}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Payouts
